import { create } from 'zustand';
import { api } from '../services/api';

// ============ Content Pillars ============

export interface ContentPillar {
  id: string;
  display_id: string;
  project_id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string | null;
  persona_fit: Record<string, boolean>;
  guide_anchors: string[];
  recommended_formats: string[];
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  stats?: PillarStats;
}

export interface PillarStats {
  totalWeeks: number;
  plannedWeeks: number;
  publishedWeeks: number;
  totalPosts: number;
}

export interface CreatePillarInput {
  project_id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  persona_fit?: Record<string, boolean>;
  guide_anchors?: string[];
  recommended_formats?: string[];
}

export interface UpdatePillarInput {
  name?: string;
  description?: string | null;
  color?: string;
  icon?: string | null;
  persona_fit?: Record<string, boolean>;
  guide_anchors?: string[];
  recommended_formats?: string[];
  is_active?: boolean;
}

// ============ Editorial Weeks ============

export type FunnelStage = 'awareness' | 'consideration' | 'activation';
export type WeekStatus = 'planned' | 'in_progress' | 'ready' | 'published' | 'skipped';
export type ContentFormat = 'carousel' | 'reel' | 'story' | 'single_graphic' | 'text';
export type CTAType = 'save' | 'link_bio' | 'dm_keyword' | 'share';

// CTA type for multi-CTA support
export interface CTA {
  type: CTAType;
  text?: string;
}

export interface EditorialWeek {
  id: string;
  display_id: string;
  project_id: string;
  year: number;
  week_number: number;
  quarter: number;
  main_topic: string;
  main_topic_subtitle: string | null;
  alt_topic: string | null;
  alt_topic_subtitle: string | null;
  // Legacy single-value fields
  pillar_id: string | null;
  flight_id: string | null;
  format: ContentFormat;
  cta_type: CTAType;
  cta_text: string | null;
  funnel_stage: FunnelStage;
  // New multi-value fields
  pillar_ids: string[];
  flight_ids: string[];
  formats: ContentFormat[];
  funnel_stages: FunnelStage[];
  ctas: CTA[];
  master_asset_type: string | null;
  target_kpis: Record<string, number>;
  planning_complete: boolean;
  status: WeekStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  pillar_name?: string;
  pillar_color?: string;
  flight_name?: string;
  flight_color?: string;
  kpis?: EditorialKPI[];
  // Linked posts and flights (loaded separately)
  posts?: LinkedPost[];
  flights?: LinkedFlight[];
}

// Simplified types for linked items
export interface LinkedPost {
  id: string;
  display_id: string;
  caption: string | null;
  scheduled_for: string | null;
  status: string;
  thumbnail_url?: string;
}

export interface LinkedFlight {
  id: string;
  display_id: string;
  name: string;
  color: string | null;
  start_date: string;
  end_date: string;
}

export interface CreateEditorialWeekInput {
  project_id: string;
  year: number;
  week_number: number;
  main_topic: string;
  main_topic_subtitle?: string;
  alt_topic?: string;
  alt_topic_subtitle?: string;
  // Legacy single-value fields
  pillar_id?: string;
  flight_id?: string;
  format?: ContentFormat;
  cta_type?: CTAType;
  cta_text?: string;
  funnel_stage?: FunnelStage;
  // New multi-value fields
  pillar_ids?: string[];
  flight_ids?: string[];
  formats?: ContentFormat[];
  funnel_stages?: FunnelStage[];
  ctas?: CTA[];
  master_asset_type?: string;
  target_kpis?: Record<string, number>;
  status?: WeekStatus;
  notes?: string;
}

export interface UpdateEditorialWeekInput {
  main_topic?: string;
  main_topic_subtitle?: string | null;
  alt_topic?: string | null;
  alt_topic_subtitle?: string | null;
  // Legacy single-value fields
  pillar_id?: string | null;
  flight_id?: string | null;
  format?: ContentFormat;
  cta_type?: CTAType;
  cta_text?: string | null;
  funnel_stage?: FunnelStage;
  // New multi-value fields
  pillar_ids?: string[];
  flight_ids?: string[];
  formats?: ContentFormat[];
  funnel_stages?: FunnelStage[];
  ctas?: CTA[];
  master_asset_type?: string | null;
  target_kpis?: Record<string, number>;
  planning_complete?: boolean;
  status?: WeekStatus;
  notes?: string | null;
}

// ============ Editorial KPIs ============

export interface EditorialKPI {
  id: string;
  editorial_week_id: string;
  post_id: string | null;
  reach: number;
  impressions: number;
  views: number;
  profile_visits: number;
  follower_growth: number;
  saves: number;
  shares: number;
  comments: number;
  story_replies: number;
  link_taps: number;
  link_clicks: number;
  shop_clicks: number;
  conversions: number;
  conversion_rate: number;
  recorded_at: string;
  notes: string | null;
}

export interface CreateKPIInput {
  post_id?: string;
  reach?: number;
  impressions?: number;
  views?: number;
  profile_visits?: number;
  follower_growth?: number;
  saves?: number;
  shares?: number;
  comments?: number;
  story_replies?: number;
  link_taps?: number;
  link_clicks?: number;
  shop_clicks?: number;
  conversions?: number;
  conversion_rate?: number;
  notes?: string;
}

// ============ Content Templates ============

export interface ContentTemplate {
  id: string;
  display_id: string;
  project_id: string;
  pillar_id: string | null;
  name: string;
  description: string | null;
  template_type: 'hook' | 'body' | 'cta' | 'full_post';
  format: ContentFormat;
  content: Record<string, unknown>;
  variables: string[];
  example_output: string | null;
  usage_notes: string | null;
  use_count: number;
  last_used_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  pillar_name?: string;
  pillar_color?: string;
}

export interface CreateTemplateInput {
  project_id: string;
  pillar_id?: string;
  name: string;
  description?: string;
  template_type?: 'hook' | 'body' | 'cta' | 'full_post';
  format?: ContentFormat;
  content?: Record<string, unknown>;
  variables?: string[];
  example_output?: string;
  usage_notes?: string;
}

export interface UpdateTemplateInput {
  pillar_id?: string | null;
  name?: string;
  description?: string | null;
  template_type?: 'hook' | 'body' | 'cta' | 'full_post';
  format?: ContentFormat;
  content?: Record<string, unknown>;
  variables?: string[];
  example_output?: string | null;
  usage_notes?: string | null;
  is_active?: boolean;
}

// ============ Statistics ============

export interface EditorialStats {
  totalWeeks: number;
  plannedWeeks: number;
  inProgressWeeks: number;
  readyWeeks: number;
  publishedWeeks: number;
  skippedWeeks: number;
  byQuarter: { quarter: number; count: number; published: number }[];
  byPillar: { pillar_id: string; pillar_name: string; count: number }[];
  byFunnelStage: { stage: string; count: number }[];
}

export interface KPIReport {
  weeks: { week_number: number; kpis: EditorialKPI | null }[];
  totals: {
    reach: number;
    impressions: number;
    saves: number;
    shares: number;
    link_clicks: number;
    conversions: number;
  };
  averages: {
    reach: number;
    saves: number;
    conversion_rate: number;
  };
}

// ============ Store State ============

interface MarketingState {
  // Pillars
  pillars: ContentPillar[];
  currentPillar: ContentPillar | null;

  // Editorial Weeks
  editorialWeeks: EditorialWeek[];
  currentWeek: EditorialWeek | null;
  currentYear: number;
  viewMode: 'year' | 'quarter' | 'month';
  selectedQuarter: number | null;

  // Calendar Filters (persistent)
  calendarFilters: {
    searchTerm: string;
    pillarId: string;
    funnelStage: string;
    status: string;
    groupBy: 'none' | 'pillar' | 'funnel' | 'status';
    weekFrom: number | '';
    weekTo: number | '';
    dateFrom: string;
    dateTo: string;
  };

  // Templates
  templates: ContentTemplate[];
  currentTemplate: ContentTemplate | null;

  // Statistics
  stats: EditorialStats | null;
  kpiReport: KPIReport | null;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Pillar Actions
  fetchPillars: (projectId: string, includeInactive?: boolean) => Promise<void>;
  fetchPillar: (id: string) => Promise<void>;
  createPillar: (data: CreatePillarInput) => Promise<ContentPillar>;
  updatePillar: (id: string, data: UpdatePillarInput) => Promise<void>;
  deletePillar: (id: string) => Promise<void>;
  reorderPillars: (projectId: string, pillarIds: string[]) => Promise<void>;

  // Editorial Week Actions
  fetchEditorialPlan: (projectId: string, year: number, quarter?: number) => Promise<void>;
  fetchEditorialWeek: (projectId: string, year: number, week: number) => Promise<void>;
  fetchEditorialWeekById: (id: string) => Promise<void>;
  createEditorialWeek: (data: CreateEditorialWeekInput) => Promise<EditorialWeek>;
  updateEditorialWeek: (id: string, data: UpdateEditorialWeekInput) => Promise<void>;
  deleteEditorialWeek: (id: string) => Promise<void>;
  moveEditorialWeek: (sourceWeekId: string, targetWeekNumber: number, projectId: string, year: number) => Promise<void>;
  bulkCreateWeeks: (projectId: string, weeks: Omit<CreateEditorialWeekInput, 'project_id'>[]) => Promise<EditorialWeek[]>;

  // KPI Actions
  addKPIs: (weekId: string, data: CreateKPIInput) => Promise<void>;
  fetchKPIReport: (projectId: string, year: number, quarter: number) => Promise<void>;

  // Template Actions
  fetchTemplates: (projectId: string, options?: { pillarId?: string; type?: string; format?: string }) => Promise<void>;
  fetchTemplate: (id: string) => Promise<void>;
  createTemplate: (data: CreateTemplateInput) => Promise<ContentTemplate>;
  updateTemplate: (id: string, data: UpdateTemplateInput) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  useTemplate: (id: string) => Promise<void>;
  applyTemplate: (id: string, variables: Record<string, string>) => Promise<{ applied_content: Record<string, unknown>; applied_example: string | null }>;
  duplicateTemplate: (id: string) => Promise<ContentTemplate>;

  // Stats Actions
  fetchStats: (projectId: string, year: number) => Promise<void>;

  // UI Actions
  setCurrentYear: (year: number) => void;
  setViewMode: (mode: 'year' | 'quarter' | 'month') => void;
  setSelectedQuarter: (quarter: number | null) => void;
  setCurrentWeek: (week: EditorialWeek | null) => void;
  setCurrentPillar: (pillar: ContentPillar | null) => void;
  setCurrentTemplate: (template: ContentTemplate | null) => void;
  setCalendarFilters: (filters: Partial<MarketingState['calendarFilters']>) => void;
  clearCalendarFilters: () => void;
  clearError: () => void;
  clearMarketingData: () => void;
}

export const useMarketingStore = create<MarketingState>((set, get) => ({
  // Initial State
  pillars: [],
  currentPillar: null,
  editorialWeeks: [],
  currentWeek: null,
  currentYear: new Date().getFullYear(),
  viewMode: 'year',
  selectedQuarter: null,
  calendarFilters: {
    searchTerm: '',
    pillarId: '',
    funnelStage: '',
    status: '',
    groupBy: 'none',
    weekFrom: '',
    weekTo: '',
    dateFrom: '',
    dateTo: '',
  },
  templates: [],
  currentTemplate: null,
  stats: null,
  kpiReport: null,
  isLoading: false,
  error: null,

  // ============ Pillar Actions ============

  fetchPillars: async (projectId, includeInactive = false) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({ project_id: projectId });
      if (includeInactive) params.append('include_inactive', 'true');
      const response = await api.get<{ data: ContentPillar[] }>(`/pillars?${params}`);
      set({ pillars: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Pillars';
      set({ error: message, isLoading: false });
    }
  },

  fetchPillar: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: ContentPillar }>(`/pillars/${id}`);
      set({ currentPillar: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des Pillars';
      set({ error: message, isLoading: false });
    }
  },

  createPillar: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: ContentPillar }>('/pillars', data);
      const newPillar = response.data;
      set((state) => ({
        pillars: [...state.pillars, newPillar],
        isLoading: false,
      }));
      return newPillar;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updatePillar: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: ContentPillar }>(`/pillars/${id}`, data);
      const updatedPillar = response.data;
      set((state) => ({
        pillars: state.pillars.map((p) => (p.id === id ? updatedPillar : p)),
        currentPillar: state.currentPillar?.id === id ? updatedPillar : state.currentPillar,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deletePillar: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/pillars/${id}`);
      set((state) => ({
        pillars: state.pillars.filter((p) => p.id !== id),
        currentPillar: state.currentPillar?.id === id ? null : state.currentPillar,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  reorderPillars: async (projectId, pillarIds) => {
    // Optimistic update
    set((state) => {
      const reordered = pillarIds
        .map((id) => state.pillars.find((p) => p.id === id))
        .filter((p): p is ContentPillar => p !== undefined);
      return { pillars: reordered };
    });

    try {
      await api.put('/pillars/reorder', { project_id: projectId, pillar_ids: pillarIds });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Sortieren';
      set({ error: message });
      throw error;
    }
  },

  // ============ Editorial Week Actions ============

  fetchEditorialPlan: async (projectId, year, quarter) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({ project_id: projectId, year: year.toString() });
      if (quarter) params.append('quarter', quarter.toString());
      const response = await api.get<{ data: EditorialWeek[] }>(`/editorial?${params}`);
      set({
        editorialWeeks: response.data,
        currentYear: year,
        selectedQuarter: quarter || null,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des Plans';
      set({ error: message, isLoading: false });
    }
  },

  fetchEditorialWeek: async (projectId, year, week) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: EditorialWeek }>(
        `/editorial/week/${year}/${week}?project_id=${projectId}`
      );
      set({ currentWeek: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Woche';
      set({ error: message, isLoading: false });
    }
  },

  fetchEditorialWeekById: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: EditorialWeek }>(`/editorial/${id}`);
      set({ currentWeek: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Woche';
      set({ error: message, isLoading: false });
    }
  },

  createEditorialWeek: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: EditorialWeek }>('/editorial/week', data);
      const newWeek = response.data;
      set((state) => ({
        editorialWeeks: [...state.editorialWeeks, newWeek].sort((a, b) => a.week_number - b.week_number),
        isLoading: false,
      }));
      return newWeek;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateEditorialWeek: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: EditorialWeek }>(`/editorial/${id}`, data);
      const updatedWeek = response.data;
      set((state) => ({
        editorialWeeks: state.editorialWeeks.map((w) => (w.id === id ? updatedWeek : w)),
        currentWeek: state.currentWeek?.id === id ? updatedWeek : state.currentWeek,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteEditorialWeek: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/editorial/${id}`);
      set((state) => ({
        editorialWeeks: state.editorialWeeks.filter((w) => w.id !== id),
        currentWeek: state.currentWeek?.id === id ? null : state.currentWeek,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  moveEditorialWeek: async (sourceWeekId, targetWeekNumber, projectId, year) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{
        data: { source: EditorialWeek | null; target: EditorialWeek | null };
      }>('/editorial/move', {
        source_week_id: sourceWeekId,
        target_week_number: targetWeekNumber,
        project_id: projectId,
        year,
      });

      const { source, target } = response.data;

      set((state) => {
        let newWeeks = [...state.editorialWeeks];

        // Remove old source week
        newWeeks = newWeeks.filter((w) => w.id !== sourceWeekId);

        // If source exists (was swapped), add it back
        if (source) {
          newWeeks.push(source);
        }

        // Add or update target week
        if (target) {
          const existingTargetIndex = newWeeks.findIndex((w) => w.id === target.id);
          if (existingTargetIndex >= 0) {
            newWeeks[existingTargetIndex] = target;
          } else {
            newWeeks.push(target);
          }
        }

        // Sort by week number
        newWeeks.sort((a, b) => a.week_number - b.week_number);

        return {
          editorialWeeks: newWeeks,
          isLoading: false,
        };
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Verschieben';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  bulkCreateWeeks: async (projectId, weeks) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: EditorialWeek[] }>('/editorial/bulk', {
        project_id: projectId,
        weeks,
      });
      const newWeeks = response.data;
      set((state) => ({
        editorialWeeks: [...state.editorialWeeks, ...newWeeks].sort((a, b) => a.week_number - b.week_number),
        isLoading: false,
      }));
      return newWeeks;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Bulk-Import';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // ============ KPI Actions ============

  addKPIs: async (weekId, data) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`/editorial/${weekId}/kpis`, data);
      // Refresh the current week to get updated KPIs
      const currentWeek = get().currentWeek;
      if (currentWeek?.id === weekId) {
        const response = await api.get<{ data: EditorialWeek }>(`/editorial/${weekId}`);
        set({ currentWeek: response.data, isLoading: false });
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erfassen der KPIs';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  fetchKPIReport: async (projectId, year, quarter) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({
        project_id: projectId,
        year: year.toString(),
        quarter: quarter.toString(),
      });
      const response = await api.get<{ data: KPIReport }>(`/editorial/kpi-report?${params}`);
      set({ kpiReport: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des KPI-Reports';
      set({ error: message, isLoading: false });
    }
  },

  // ============ Template Actions ============

  fetchTemplates: async (projectId, options = {}) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({ project_id: projectId });
      if (options.pillarId) params.append('pillar_id', options.pillarId);
      if (options.type) params.append('type', options.type);
      if (options.format) params.append('format', options.format);
      const response = await api.get<{ data: ContentTemplate[] }>(`/templates?${params}`);
      set({ templates: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Templates';
      set({ error: message, isLoading: false });
    }
  },

  fetchTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: ContentTemplate }>(`/templates/${id}`);
      set({ currentTemplate: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des Templates';
      set({ error: message, isLoading: false });
    }
  },

  createTemplate: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: ContentTemplate }>('/templates', data);
      const newTemplate = response.data;
      set((state) => ({
        templates: [newTemplate, ...state.templates],
        isLoading: false,
      }));
      return newTemplate;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateTemplate: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: ContentTemplate }>(`/templates/${id}`, data);
      const updatedTemplate = response.data;
      set((state) => ({
        templates: state.templates.map((t) => (t.id === id ? updatedTemplate : t)),
        currentTemplate: state.currentTemplate?.id === id ? updatedTemplate : state.currentTemplate,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/templates/${id}`);
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        currentTemplate: state.currentTemplate?.id === id ? null : state.currentTemplate,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  useTemplate: async (id) => {
    try {
      await api.post(`/templates/${id}/use`);
      // Update use_count locally
      set((state) => ({
        templates: state.templates.map((t) =>
          t.id === id ? { ...t, use_count: t.use_count + 1, last_used_at: new Date().toISOString() } : t
        ),
      }));
    } catch (error) {
      console.error('Error recording template usage:', error);
    }
  },

  applyTemplate: async (id, variables) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{
        data: { applied_content: Record<string, unknown>; applied_example: string | null };
      }>(`/templates/${id}/apply`, { variables });
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Anwenden';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  duplicateTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: ContentTemplate }>(`/templates/${id}/duplicate`);
      const newTemplate = response.data;
      set((state) => ({
        templates: [newTemplate, ...state.templates],
        isLoading: false,
      }));
      return newTemplate;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Duplizieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // ============ Stats Actions ============

  fetchStats: async (projectId, year) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: EditorialStats }>(
        `/editorial/stats/${year}?project_id=${projectId}`
      );
      set({ stats: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Statistiken';
      set({ error: message, isLoading: false });
    }
  },

  // ============ UI Actions ============

  setCurrentYear: (year) => set({ currentYear: year }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setSelectedQuarter: (quarter) => set({ selectedQuarter: quarter }),
  setCurrentWeek: (week) => set({ currentWeek: week }),
  setCurrentPillar: (pillar) => set({ currentPillar: pillar }),
  setCurrentTemplate: (template) => set({ currentTemplate: template }),
  setCalendarFilters: (filters) =>
    set((state) => ({
      calendarFilters: { ...state.calendarFilters, ...filters },
    })),
  clearCalendarFilters: () =>
    set({
      calendarFilters: {
        searchTerm: '',
        pillarId: '',
        funnelStage: '',
        status: '',
        groupBy: 'none',
        weekFrom: '',
        weekTo: '',
        dateFrom: '',
        dateTo: '',
      },
    }),
  clearError: () => set({ error: null }),
  clearMarketingData: () =>
    set({
      pillars: [],
      currentPillar: null,
      editorialWeeks: [],
      currentWeek: null,
      templates: [],
      currentTemplate: null,
      stats: null,
      kpiReport: null,
    }),
}));
